import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { chatWithAIStream, fetchPublishedModels, fetchPublishedModelDetail, manageModelInteractions } from '../lib/api';
import { BookOpen, MessageCircle, Edit3, Search, Filter, ArrowLeft, Send, X, Minimize2, Maximize2 } from 'lucide-react';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import '../styles/modelContent.css';
import { processModelContent } from '../utils/htmlContentProcessor';

interface Model {
  id: string;
  name: string;
  description: string;
  content: string;
  tags: string[];
  created_at: string;
}

const DashboardModelsList: React.FC = () => {
  const [models, setModels] = useState<Model[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    try {
      const response = await fetchPublishedModels({
        search: searchTerm || undefined,
        tag: selectedTag || undefined
      });
      if (response.success) {
        setModels(response.data?.models || []);
      } else {
        throw new Error(response.error || '获取模型失败');
      }
    } catch (error) {
      console.error('获取模型失败:', error);
      toast.error('获取模型失败');
    } finally {
      setLoading(false);
    }
  };

  const filteredModels = models.filter(model => {
    const matchesSearch = model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         model.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTag = selectedTag === '' || model.tags.includes(selectedTag);
    return matchesSearch && matchesTag;
  });

  const allTags = Array.from(new Set(models.flatMap(model => model.tags)));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">模型工厂</h1>
        <p className="text-lg text-gray-600 mb-6">
          探索和学习各种思维模型，提升认知能力和决策水平
        </p>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="搜索模型..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white min-w-[150px]"
            >
              <option value="">所有标签</option>
              {allTags.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Models Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredModels.map((model) => (
          <div
            key={model.id}
            onClick={() => navigate(`/dashboard/models/${model.id}`)}
            className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <BookOpen className="w-8 h-8 text-blue-600 flex-shrink-0" />
                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                </div>
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                {model.name}
              </h3>
              
              <p className="text-gray-600 mb-4 line-clamp-3">
                {model.description}
              </p>
              
              {model.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {model.tags.slice(0, 3).map((tag, index) => (
                    <span
                      key={index}
                      className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                  {model.tags.length > 3 && (
                    <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                      +{model.tags.length - 3}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredModels.length === 0 && (
        <div className="text-center py-12">
          <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            没有找到匹配的模型
          </h3>
          <p className="text-gray-600">
            尝试调整搜索条件或标签筛选
          </p>
        </div>
      )}
    </div>
  );
};

const DashboardModelDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [model, setModel] = useState<Model | null>(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  
  // AI Chat state
  const [showChat, setShowChat] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(400);
  const [isResizing, setIsResizing] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState<Array<{question: string, answer: string}>>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [chatMinimized, setChatMinimized] = useState(false);

  const presetQuestions = [
    "这个模型的应用场景是什么？",
    "如何更好地吸收和训练这个思维模型？"
  ];

  useEffect(() => {
    if (id) {
      fetchModel(id);
      loadChatHistory(id);
    }
  }, [id]);

  // Handle resize
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsResizing(true);
    e.preventDefault();
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing) return;
    
    const newWidth = window.innerWidth - e.clientX;
    if (newWidth >= 300 && newWidth <= 800) {
      setSidebarWidth(newWidth);
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const fetchModel = async (modelId: string) => {
    try {
      const response = await fetchPublishedModelDetail(modelId);
      if (response.success && response.data?.model) {
        setModel(response.data.model);
      } else {
        throw new Error(response.error || '模型不存在');
      }
    } catch (error) {
      console.error('获取模型失败:', error);
      toast.error('获取模型失败');
      navigate('/dashboard/models');
    } finally {
      setLoading(false);
    }
  };

  const loadChatHistory = async (modelId: string) => {
    try {
      const response = await manageModelInteractions('chat_history', modelId);
      if (response.success && response.data?.chat_history) {
        const history = response.data.chat_history.map((chat: any) => ({
          question: chat.question,
          answer: chat.answer
        }));
        setChatHistory(history);
      }
    } catch (error) {
      console.error('加载聊天历史失败:', error);
    }
  };

  const saveNotes = async () => {
    if (!model || !notes.trim() || !user) return;

    try {
      const response = await manageModelInteractions('save_notes', model.id, { notes });
      if (response.success) {
        toast.success('笔记保存成功！');
        setNotes('');
      } else {
        throw new Error(response.error || '保存笔记失败');
      }
    } catch (error) {
      console.error('保存笔记失败:', error);
      toast.error('保存笔记失败');
    }
  };

  const sendChatMessage = async (message?: string) => {
    if (!model || chatLoading || isStreaming) return;
    
    const userMessage = message || chatMessage.trim();
    if (!userMessage) return;

    setChatMessage('');
    setIsStreaming(true);
    setStreamingMessage('');

    // 添加用户消息到历史
    const newUserChat = { question: userMessage, answer: '' };
    setChatHistory(prev => [...prev, newUserChat]);

    try {
      await chatWithAIStream(
        userMessage,
        model.content,
        'model_discussion',
        undefined,
        // onChunk
        (chunk: string) => {
          setStreamingMessage(prev => prev + chunk);
        },
        // onComplete
        async (fullResponse: string) => {
          try {
            // 保存聊天记录到数据库
            const response = await manageModelInteractions('save_chat', model.id, {
              user_question: userMessage,
              ai_answer: fullResponse
            });

            if (!response.success) {
              console.error('保存聊天记录失败:', response.error);
            }

            // 更新聊天历史
            setChatHistory(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = { question: userMessage, answer: fullResponse };
              return updated;
            });
            setStreamingMessage('');
          } catch (error) {
            console.error('处理聊天完成失败:', error);
          }
        },
        // onError
        (error: string) => {
          toast.error('AI回复失败: ' + error);
          setStreamingMessage('');
          // 移除失败的消息
          setChatHistory(prev => prev.slice(0, -1));
        }
      );
    } catch (error) {
      console.error('发送消息失败:', error);
      toast.error('发送消息失败');
      setStreamingMessage('');
      setChatHistory(prev => prev.slice(0, -1));
    } finally {
      setIsStreaming(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!model) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">模型不存在</h3>
          <Link to="/dashboard/models" className="text-blue-600 hover:text-blue-700">
            返回模型列表
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex relative">
      {/* Main Content */}
      <div className="flex-1 transition-all duration-300" style={{ marginRight: showChat ? `${sidebarWidth}px` : '0' }}>
        <div className="p-6">
          {/* Header */}
          <div className="mb-6">
            <Link
              to="/dashboard/models"
              className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-3"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              返回模型列表
            </Link>
            
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex-1">
                  <h1 className="text-xl font-bold text-gray-900 mb-1">
                    {model.name}
                  </h1>
                  <p className="text-sm text-gray-600 mb-2">{model.description}</p>
                  {model.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {model.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <BookOpen className="w-8 h-8 text-blue-600 flex-shrink-0" />
              </div>
              
              {/* AI讨论按钮 */}
              <div className="flex justify-end">
                <button
                  onClick={() => setShowChat(!showChat)}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  {showChat ? '关闭AI讨论' : '开启AI讨论'}
                </button>
              </div>
            </div>
          </div>

          {/* Model Content */}
          <div className="bg-white rounded-lg shadow-sm mb-6">
            <div className="p-6">
              <div className="prose max-w-none">
                <ReactMarkdown
                  components={{
                    p: ({ children }) => <p className="mb-4 last:mb-0 text-gray-700 leading-relaxed">{children}</p>,
                    ul: ({ children }) => <ul className="list-disc list-inside mb-4 space-y-2 text-gray-700">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal list-inside mb-4 space-y-2 text-gray-700">{children}</ol>,
                    li: ({ children }) => <li className="text-gray-700">{children}</li>,
                    h1: ({ children }) => <h1 className="text-2xl font-bold mb-4 text-gray-900">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-xl font-bold mb-3 text-gray-900">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-lg font-bold mb-2 text-gray-900">{children}</h3>,
                    strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
                    em: ({ children }) => <em className="italic text-gray-700">{children}</em>,
                    code: ({ children }) => <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono text-gray-800">{children}</code>,
                    pre: ({ children }) => <pre className="bg-gray-100 p-4 rounded text-sm overflow-x-auto mb-4">{children}</pre>,
                    blockquote: ({ children }) => <blockquote className="border-l-4 border-blue-500 pl-4 italic mb-4 text-gray-600">{children}</blockquote>,
                  }}
                >
                  {(() => {
                    const { processedContent, contentType } = processModelContent(model.content);
                    
                    if (contentType === 'html') {
                      return (
                        <div 
                          dangerouslySetInnerHTML={{ __html: processedContent }}
                          className="model-html-content"
                        />
                      );
                    } else {
                      return processedContent;
                    }
                  })()}
                </ReactMarkdown>
              </div>
            </div>
          </div>

          {/* Notes Section */}
          <div className="bg-white rounded-lg shadow-sm mb-6">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Edit3 className="w-5 h-5 mr-2" />
                记录笔记
              </h3>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="记录你对这个模型的理解和思考..."
                className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
              <div className="flex justify-end mt-4">
                <button
                  onClick={saveNotes}
                  disabled={!notes.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  保存笔记
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Chat Sidebar */}
      {showChat && (
        <div 
          className="fixed top-0 right-0 h-full bg-white shadow-xl border-l border-gray-200 z-50 flex"
          style={{ width: `${sidebarWidth}px` }}
        >
          {/* Resize Handle */}
          <div
            className="w-1 bg-gray-200 hover:bg-blue-400 cursor-col-resize flex-shrink-0 transition-colors"
            onMouseDown={handleMouseDown}
          />
          
          {/* Chat Content */}
          <div className="flex-1 flex flex-col h-full">
            {/* Chat Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
              <h4 className="font-medium text-gray-900 flex items-center">
                <MessageCircle className="w-4 h-4 mr-2" />
                AI讨论 - {model.name}
              </h4>
              <button
                onClick={() => setShowChat(false)}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Preset Questions */}
              {chatHistory.length === 0 && !isStreaming && (
                <div className="space-y-3 mb-6">
                  <p className="text-sm text-gray-600 mb-3">快速开始：</p>
                  {presetQuestions.map((question, index) => (
                    <button
                      key={index}
                      onClick={() => sendChatMessage(question)}
                      className="block w-full text-left p-3 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              )}

              {/* Chat History */}
              {chatHistory.map((chat, index) => (
                <div key={index} className="space-y-3 mb-6">
                  {/* User Message */}
                  <div className="flex justify-end">
                    <div className="max-w-[80%] bg-blue-600 text-white rounded-lg px-4 py-3 text-sm">
                      {chat.question}
                    </div>
                  </div>
                  {/* AI Message */}
                  {chat.answer && (
                    <div className="flex justify-start">
                      <div className="max-w-[85%] bg-gray-100 text-gray-900 rounded-lg px-4 py-3 text-sm">
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                            ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
                            ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
                            li: ({ children }) => <li className="text-sm">{children}</li>,
                            h1: ({ children }) => <h1 className="text-base font-bold mb-2">{children}</h1>,
                            h2: ({ children }) => <h2 className="text-sm font-bold mb-2">{children}</h2>,
                            h3: ({ children }) => <h3 className="text-sm font-bold mb-1">{children}</h3>,
                            strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                            em: ({ children }) => <em className="italic">{children}</em>,
                            code: ({ children }) => <code className="bg-gray-200 px-1 py-0.5 rounded text-xs font-mono">{children}</code>,
                            pre: ({ children }) => <pre className="bg-gray-200 p-2 rounded text-xs overflow-x-auto mb-2">{children}</pre>,
                            blockquote: ({ children }) => <blockquote className="border-l-4 border-gray-300 pl-3 italic mb-2">{children}</blockquote>,
                          }}
                        >
                          {chat.answer}
                        </ReactMarkdown>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Streaming Message */}
              {isStreaming && (
                <div className="space-y-3 mb-6">
                  <div className="flex justify-start">
                    <div className="max-w-[85%] bg-gray-100 text-gray-900 rounded-lg px-4 py-3 text-sm">
                      {streamingMessage ? (
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                            ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
                            ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
                            li: ({ children }) => <li className="text-sm">{children}</li>,
                            strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                            em: ({ children }) => <em className="italic">{children}</em>,
                            code: ({ children }) => <code className="bg-gray-200 px-1 py-0.5 rounded text-xs font-mono">{children}</code>,
                          }}
                        >
                          {streamingMessage}
                        </ReactMarkdown>
                      ) : null}
                      <div className="flex items-center mt-2">
                        <div className="animate-pulse flex space-x-1">
                          <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                          <div className="w-2 h-2 bg-blue-600 rounded-full" style={{ animationDelay: '0.2s' }}></div>
                          <div className="w-2 h-2 bg-blue-600 rounded-full" style={{ animationDelay: '0.4s' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Chat Input */}
            <div className="border-t border-gray-200 p-4 flex-shrink-0">
              <div className="flex space-x-3">
                <input
                  type="text"
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="向AI提问关于这个模型的问题..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                  disabled={chatLoading || isStreaming}
                />
                <button
                  onClick={() => sendChatMessage()}
                  disabled={!chatMessage.trim() || chatLoading || isStreaming}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                >
                  {isStreaming ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

const DashboardModels: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  
  if (id) {
    return <DashboardModelDetail />;
  }
  
  return <DashboardModelsList />;
};

export default DashboardModels;